import React, { useState } from "react";
import { useAuth, API } from "@/context/AuthContext";
import { Copy, Check, Puzzle, Download, Terminal, Zap } from "lucide-react";
import { toast } from "sonner";

export default function Extension() {
    const { token } = useAuth();
    const [copied, setCopied] = useState(null);
    const backend = process.env.REACT_APP_BACKEND_URL;

    const copy = async (text, id) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(id);
            toast.success(id === "config" ? "Config copied — paste it in the extension popup" : "Copied");
            setTimeout(() => setCopied(null), 1600);
        } catch {
            toast.error("Copy failed");
        }
    };

    const apiUrl = `${backend}/api`;
    const configBlob = JSON.stringify({ apiUrl, apiToken: token || "" }, null, 2);

    return (
        <div data-testid="extension-page">
            <div className="mb-8">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-signal mb-2">/ CHROME EXTENSION</div>
                <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tight">Install the extension.</h1>
                <p className="text-zinc-400 mt-2">Load the SalesWhisper helper right inside LinkedIn and Instagram DMs.</p>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <div className="border border-zinc-900 bg-[#0c0c0c] p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <Puzzle className="w-5 h-5 text-signal" />
                        <h2 className="font-heading font-bold text-xl">1. Download</h2>
                    </div>
                    <p className="text-sm text-zinc-400 leading-relaxed mb-5">
                        Download the unpacked Chrome extension as a zip, then unzip it on your machine.
                    </p>
                    <a
                        href={`${API}/extension/download`}
                        download
                        data-testid="download-extension-btn"
                        className="inline-flex items-center gap-2 bg-signal hover:bg-signal-hover text-white px-4 py-2.5 rounded-md text-sm font-semibold transition-colors"
                    >
                        <Download className="w-4 h-4" />
                        Download extension (.zip)
                    </a>
                </div>

                <div className="border border-zinc-900 bg-[#0c0c0c] p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <Terminal className="w-5 h-5 text-signal" />
                        <h2 className="font-heading font-bold text-xl">2. Load unpacked</h2>
                    </div>
                    <ol className="text-sm text-zinc-300 space-y-2 list-decimal pl-5 leading-relaxed">
                        <li>Unzip the downloaded file</li>
                        <li>Open <code className="text-signal font-mono">chrome://extensions</code></li>
                        <li>Toggle <span className="text-white font-semibold">Developer mode</span> (top right)</li>
                        <li>Click <span className="text-white font-semibold">Load unpacked</span></li>
                        <li>Select the unzipped <code className="text-signal font-mono">saleswhisper-extension</code> folder</li>
                        <li>Pin the SalesWhisper icon to your toolbar</li>
                    </ol>
                </div>

                {/* ONE-STEP CONFIG — replaces the old two-field copy dance */}
                <div className="border border-signal/40 bg-signal/5 p-6 lg:col-span-2" data-testid="extension-config-card">
                    <div className="flex items-center gap-3 mb-4">
                        <Zap className="w-5 h-5 text-signal" strokeWidth={2} />
                        <h2 className="font-heading font-bold text-xl">3. Copy your config (one click)</h2>
                    </div>
                    <p className="text-sm text-zinc-300 mb-4">
                        Copy the block below, open the SalesWhisper extension popup, and paste it into <span className="text-signal font-semibold">"Paste config from SalesWhisper"</span>. Both fields auto-fill — no more copying twice.
                    </p>
                    <div className="flex items-stretch gap-2 mb-3">
                        <pre
                            data-testid="extension-config-block"
                            className="flex-1 bg-zinc-950 border border-zinc-900 rounded-md px-3 py-2.5 text-[11px] font-mono text-zinc-200 overflow-auto whitespace-pre"
                        >
{configBlob}
                        </pre>
                        <button
                            onClick={() => copy(configBlob, "config")}
                            data-testid="copy-config-btn"
                            disabled={!token}
                            className="bg-signal hover:bg-signal-hover text-white px-5 py-2 rounded-md text-sm font-semibold flex items-center gap-2 transition-colors disabled:opacity-50"
                        >
                            {copied === "config" ? (
                                <><Check className="w-4 h-4" /> Copied</>
                            ) : (
                                <><Copy className="w-4 h-4" /> Copy config</>
                            )}
                        </button>
                    </div>
                    {!token && (
                        <p className="text-xs text-amber-400/80 font-mono">Sign in first to generate your token.</p>
                    )}

                    {/* Optional legacy: individual copy buttons for users who prefer it */}
                    <details className="mt-5 text-sm">
                        <summary className="cursor-pointer text-zinc-400 hover:text-white text-xs font-mono uppercase tracking-widest">
                            · Prefer to paste each field manually? Copy individually
                        </summary>
                        <div className="space-y-3 mt-4">
                            <div>
                                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">API Base URL</div>
                                <div className="flex items-center gap-2">
                                    <code className="flex-1 bg-zinc-950 border border-zinc-900 rounded-md px-3 py-2.5 text-xs font-mono text-zinc-200 truncate">
                                        {apiUrl}
                                    </code>
                                    <button
                                        onClick={() => copy(apiUrl, "api")}
                                        data-testid="copy-api-url"
                                        className="border border-zinc-800 hover:border-zinc-600 text-zinc-300 hover:text-white px-3 py-2 rounded-md text-xs"
                                    >
                                        {copied === "api" ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Your access token</div>
                                <div className="flex items-center gap-2">
                                    <code className="flex-1 bg-zinc-950 border border-zinc-900 rounded-md px-3 py-2.5 text-xs font-mono text-zinc-200 truncate">
                                        {token || "— sign in first —"}
                                    </code>
                                    <button
                                        onClick={() => token && copy(token, "token")}
                                        data-testid="copy-token"
                                        className="border border-zinc-800 hover:border-zinc-600 text-zinc-300 hover:text-white px-3 py-2 rounded-md text-xs disabled:opacity-50"
                                        disabled={!token}
                                    >
                                        {copied === "token" ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </details>

                    <p className="text-xs text-zinc-500 mt-4">
                        Keep your token private — anyone with it can generate replies on your account.
                    </p>
                </div>
            </div>
        </div>
    );
}
